import {
  useGetMonthlyAnalyticsQuery,
  useGetWeeklyAnalyticsQuery,
  useGetYearlyAnalyticsQuery,
} from '@/store/analytic-api'
import { AnalyticsPeriodResult, AnalyticsResult, GetAnalyticsResponse } from '@/store/analytic.types'

export enum Period {
  Weekly = 'weekly',
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export const PERIODS: readonly Period[] = [Period.Weekly, Period.Monthly, Period.Yearly]

export const PERIOD_LABELS: Record<Period, string> = {
  [Period.Weekly]: 'Nedeljno',
  [Period.Monthly]: 'Mesečno',
  [Period.Yearly]: 'Godišnje',
}

export interface AnalyticsPeriod {
  periods: AnalyticsResult[]
}

export function isAnalyticsPeriodResult(
  data: GetAnalyticsResponse | undefined,
): data is AnalyticsPeriodResult {
  return (
    data !== null &&
    typeof data === 'object' &&
    'periods' in data &&
    Array.isArray((data as unknown as { periods?: unknown }).periods)
  )
}

export function shiftDate(date: Date, period: Period, amount: number): Date {
  const d = new Date(date)
  switch (period) {
    case Period.Weekly:
      d.setDate(d.getDate() + 7 * amount)
      break
    case Period.Monthly:
      d.setMonth(d.getMonth() + amount)
      break
    case Period.Yearly:
      d.setFullYear(d.getFullYear() + amount)
      break
  }
  return d
}

export function getDateStart(period: Period, reference: Date): string {
  const d = new Date(reference)

  switch (period) {
    case Period.Weekly: {
      const day = d.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const diff = day === 0 ? -6 : 1 - day // move to Monday
      d.setDate(d.getDate() + diff)
      break
    }
    case Period.Monthly:
      d.setDate(1)
      break
    case Period.Yearly:
      d.setMonth(0, 1)
      break
  }

  return d.toLocaleDateString('en-CA')
}

export function formatDateLocalized(dateStr: string): string {
  const d = new Date(dateStr)
  const localized = d.toLocaleDateString('sr-Latn-RS', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return localized.charAt(0).toUpperCase() + localized.slice(1)
}

export function useAnalyticsPeriodQuery(
  period: Period,
  date: Date,
): {
  data: GetAnalyticsResponse | undefined
  dateStart: string
} {
  const dateStart = getDateStart(period, date)

  const weeklyQuery = useGetWeeklyAnalyticsQuery(
    { dateStart: period === Period.Weekly ? dateStart : '' },
    { skip: period !== Period.Weekly },
  )
  const monthlyQuery = useGetMonthlyAnalyticsQuery(
    { dateStart: period === Period.Monthly ? dateStart : '' },
    { skip: period !== Period.Monthly },
  )
  const yearlyQuery = useGetYearlyAnalyticsQuery(
    { dateStart: period === Period.Yearly ? dateStart : '' },
    { skip: period !== Period.Yearly },
  )

  const data =
    period === Period.Weekly
      ? weeklyQuery.data
      : period === Period.Monthly
        ? monthlyQuery.data
        : yearlyQuery.data

  return { data, dateStart }
}

export function getMonthlySummaryStats(data: GetAnalyticsResponse | undefined): {
  totalMessages: number
  totalThreads: number
  totalThreadDuration: number
  averageThreadDuration: number
  averageThreadMessages: number
} {
  if (!data || !isAnalyticsPeriodResult(data)) {
    return {
      totalMessages: 0,
      totalThreads: 0,
      totalThreadDuration: 0,
      averageThreadDuration: 0,
      averageThreadMessages: 0,
    }
  }

  const totalMessages = data.periods.reduce((acc, cur) => acc + cur.total_messages, 0)
  const totalThreads = data.periods.reduce((acc, cur) => acc + cur.total_threads, 0)
  const totalThreadDuration = data.periods.reduce((acc, cur) => acc + cur.total_thread_duration, 0)

  const averageThreadDuration = totalThreads > 0 ? totalThreadDuration / totalThreads : 0
  const averageThreadMessages = totalThreads > 0 ? totalMessages / totalThreads : 0

  return {
    totalMessages,
    totalThreads,
    totalThreadDuration,
    averageThreadDuration,
    averageThreadMessages,
  }
}

export function fillMissingMonths(data: AnalyticsPeriodResult): AnalyticsResult[] {
  const allMonths = Array.from(
    { length: 12 },
    (_, i) => new Date(data.from).getFullYear() + `-${String(i + 1).padStart(2, '0')}-01`,
  )
  const map = new Map(data.periods.map((p) => [p.date, p]))

  return allMonths.map((date) => {
    const item = map.get(date)
    return (
      item ?? {
        date,
        total_messages: 0,
        total_threads: 0,
        total_thread_duration: 0,
        average_thread_duration: 0,
        average_thread_messages: 0,
      }
    )
  })
}

export function fillMissingWeekDays(data: AnalyticsPeriodResult): AnalyticsResult[] {
  const weekStart = new Date(data.from)
  const allDays: string[] = []

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + i)
    const isoDate = d.toISOString().slice(0, 10)
    allDays.push(isoDate)
  }

  const map = new Map(data.periods.map((p) => [p.date.slice(0, 10), p]))

  return allDays.map((date) => {
    const existing = map.get(date)
    return (
      existing ?? {
        date,
        total_messages: 0,
        total_threads: 0,
        total_thread_duration: 0,
        average_thread_duration: 0,
        average_thread_messages: 0,
      }
    )
  })
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return Number.isInteger(seconds) ? `${seconds}s` : `${seconds.toFixed(2)}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  const formattedSeconds = Number.isInteger(remainingSeconds)
    ? `${remainingSeconds}s`
    : `${remainingSeconds.toFixed(2)}s`

  return `${minutes}min ${formattedSeconds}`
}

export function getDefaultReferenceDate(period: Period): Date {
  const today = new Date()
  switch (period) {
    case Period.Monthly:
      return new Date(today.getFullYear(), today.getMonth(), 1)
    case Period.Yearly:
      return new Date(today.getFullYear(), 0, 1)
    case Period.Weekly:
    default:
      return today
  }
}

export function getSortedPeriods(
  data: AnalyticsPeriodResult,
  period: Period,
): AnalyticsPeriodResult['periods'] {
  if (period === Period.Yearly) return fillMissingMonths(data)
  if (period === Period.Weekly) return fillMissingWeekDays(data)
  return [...data.periods].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}
